import React from "react";
import {
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import type { ActionPlanBlock } from "../../api/types";

interface ActionPlanViewProps {
  block: ActionPlanBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

/**
 * AI-authored action plan rendered as a checklist. No data fetch — items are
 * generated on demand and stored on the block.
 */
export const ActionPlanView: React.FC<ActionPlanViewProps> = ({
  block,
  branding,
}) => {
  const theme = useTheme();
  const accent = branding?.primaryColor ?? theme.palette.primary.main;

  if (!block.items || block.items.length === 0) {
    return (
      <Alert severity="info">
        No action plan generated yet — use Generate with AI.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {block.title}
        </Typography>
      )}
      <List>
        {block.items.map((item, i) => (
          <ListItem key={i} alignItems="flex-start" disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckCircleOutlineIcon sx={{ color: accent }} />
            </ListItemIcon>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
